import * as z from 'zod'
import { postgrestFrom, runPostgrest } from '@/api/postgrest/client'
import {
  producttypeCoverageKeywordRowSchema,
  producttypeCoverageSalesRowSchema,
  producttypeKeywordCountRowSchema,
  producttypeKeywordTrafficRowSchema,
  producttypeMasterRowSchema,
  producttypeTrafficAggregateRowSchema,
} from '@/api/keywordCoverage/schemas'
import type {
  KeywordCoveragePage,
  KeywordCoverageRow,
  KeywordTrafficCoveragePage,
  KeywordTrafficCoverageRow,
  ProducttypeCoverageKeywordRow,
  ProducttypeCoverageSalesRow,
  ProducttypeKeywordCountRow,
  ProducttypeKeywordTrafficRow,
  ProducttypeMasterRow,
  ProducttypeTrafficAggregateRow,
} from '@/api/keywordCoverage/types'

const QUERY_PAGE_SIZE = 500

export type KeywordCoverageSort =
  'sales' | 'total' | 'primary' | 'secondary' | 'emotion' | 'longtail' | 'alias'

export type SortDirection = 'asc' | 'desc'

async function fetchAllTrafficAggregates(
  sort: Exclude<KeywordCoverageSort, 'sales'>,
  signal?: AbortSignal,
): Promise<readonly ProducttypeTrafficAggregateRow[]> {
  const rows: ProducttypeTrafficAggregateRow[] = []

  for (let from = 0; ; from += QUERY_PAGE_SIZE) {
    let query = postgrestFrom('producttype_keywords')
      .select(
        'producttypeKey:producttype_key,...keywords!inner(traffic:search_volume_per_month_avg.sum())',
      )
      .order('producttype_key', { ascending: true })
      .range(from, from + QUERY_PAGE_SIZE - 1)

    switch (sort) {
      case 'primary':
        query = query.eq('keywords.keyword_type', 0)
        break
      case 'secondary':
        query = query.eq('keywords.keyword_type', 1)
        break
      case 'emotion':
        query = query.eq('keywords.keyword_type', 2)
        break
      case 'longtail':
        query = query.eq('keywords.keyword_type', 3)
        break
      case 'alias':
        query = query.eq('keywords.is_alias', true)
        break
      case 'total':
        break
    }
    if (signal !== undefined) query = query.abortSignal(signal)

    const page = await runPostgrest(
      query,
      z.array(producttypeTrafficAggregateRowSchema),
      'producttype_keywords Traffic-Summe',
    )
    rows.push(...page)
    if (page.length < QUERY_PAGE_SIZE) return rows
  }
}

async function fetchAllKeywordCounts(
  sort: Exclude<KeywordCoverageSort, 'sales'>,
  signal?: AbortSignal,
): Promise<readonly ProducttypeKeywordCountRow[]> {
  const rows: ProducttypeKeywordCountRow[] = []

  for (let from = 0; ; from += QUERY_PAGE_SIZE) {
    let query = postgrestFrom('producttype_keywords')
      .select(
        sort === 'total'
          ? 'producttypeKey:producttype_key,keywordCount:count()'
          : 'producttypeKey:producttype_key,keywordCount:count(),keywords!inner()',
      )
      .order('producttype_key', { ascending: true })
      .range(from, from + QUERY_PAGE_SIZE - 1)

    switch (sort) {
      case 'primary':
        query = query.eq('keywords.keyword_type', 0)
        break
      case 'secondary':
        query = query.eq('keywords.keyword_type', 1)
        break
      case 'emotion':
        query = query.eq('keywords.keyword_type', 2)
        break
      case 'longtail':
        query = query.eq('keywords.keyword_type', 3)
        break
      case 'alias':
        query = query.eq('keywords.is_alias', true)
        break
      case 'total':
        break
    }
    if (signal !== undefined) query = query.abortSignal(signal)

    const page = await runPostgrest(
      query,
      z.array(producttypeKeywordCountRowSchema),
      'producttype_keywords Keyword-Anzahl',
    )
    rows.push(...page)
    if (page.length < QUERY_PAGE_SIZE) return rows
  }
}

async function fetchAllProducttypes(
  signal?: AbortSignal,
): Promise<readonly ProducttypeMasterRow[]> {
  const rows: ProducttypeMasterRow[] = []

  for (let from = 0; ; from += QUERY_PAGE_SIZE) {
    let query = postgrestFrom('producttypes')
      .select(
        'producttypeKey:producttypes_key,producttypeName:name,description,aiInstructions:AiInstructions,deactivated,state:states(name)',
      )
      .order('producttypes_key', { ascending: true })
      .range(from, from + QUERY_PAGE_SIZE - 1)
    if (signal !== undefined) query = query.abortSignal(signal)

    const page = await runPostgrest(query, z.array(producttypeMasterRowSchema), 'producttypes')
    rows.push(...page)
    if (page.length < QUERY_PAGE_SIZE) return rows
  }
}

async function fetchAllSales(
  signal?: AbortSignal,
): Promise<readonly ProducttypeCoverageSalesRow[]> {
  const rows: ProducttypeCoverageSalesRow[] = []

  for (let from = 0; ; from += QUERY_PAGE_SIZE) {
    let query = postgrestFrom('producttype_sales')
      .select('producttypeKey:producttype_key,sales365:sales_365')
      .order('producttype_key', { ascending: true })
      .range(from, from + QUERY_PAGE_SIZE - 1)
    if (signal !== undefined) query = query.abortSignal(signal)

    const page = await runPostgrest(
      query,
      z.array(producttypeCoverageSalesRowSchema),
      'producttype_sales',
    )
    rows.push(...page)
    if (page.length < QUERY_PAGE_SIZE) return rows
  }
}

async function fetchKeywords(
  producttypeKeys: readonly string[],
  signal?: AbortSignal,
): Promise<readonly ProducttypeCoverageKeywordRow[]> {
  const rows: ProducttypeCoverageKeywordRow[] = []

  for (let from = 0; ; from += QUERY_PAGE_SIZE) {
    let query = postgrestFrom('producttype_keywords')
      .select(
        'producttypeKey:producttype_key,keyword:keywords!inner(keywordType:keyword_type,isAlias:is_alias)',
      )
      .in('producttype_key', producttypeKeys)
      .order('producttype_key', { ascending: true })
      .order('keyword_id', { ascending: true })
      .range(from, from + QUERY_PAGE_SIZE - 1)
    if (signal !== undefined) query = query.abortSignal(signal)

    const page = await runPostgrest(
      query,
      z.array(producttypeCoverageKeywordRowSchema),
      'producttype_keywords',
    )
    rows.push(...page)
    if (page.length < QUERY_PAGE_SIZE) return rows
  }
}

async function fetchKeywordTraffic(
  producttypeKeys: readonly string[],
  signal?: AbortSignal,
): Promise<readonly ProducttypeKeywordTrafficRow[]> {
  const rows: ProducttypeKeywordTrafficRow[] = []

  for (let from = 0; ; from += QUERY_PAGE_SIZE) {
    let query = postgrestFrom('producttype_keywords')
      .select(
        'producttypeKey:producttype_key,keyword:keywords!inner(keywordType:keyword_type,isAlias:is_alias,traffic:search_volume_per_month_avg)',
      )
      .in('producttype_key', producttypeKeys)
      .order('producttype_key', { ascending: true })
      .order('keyword_id', { ascending: true })
      .range(from, from + QUERY_PAGE_SIZE - 1)
    if (signal !== undefined) query = query.abortSignal(signal)

    const page = await runPostgrest(
      query,
      z.array(producttypeKeywordTrafficRowSchema),
      'producttype_keywords Traffic',
    )
    rows.push(...page)
    if (page.length < QUERY_PAGE_SIZE) return rows
  }
}

function isActiveProducttype(row: ProducttypeMasterRow): boolean {
  if (row.deactivated === true) return false

  const stateName = row.state?.name.trim().toLocaleLowerCase('de') ?? ''
  return !['offline', 'deleted', 'gelöscht', 'geloescht'].some((excludedName) =>
    stateName.includes(excludedName),
  )
}

interface MutableCounts {
  totalCount: number
  primaryCount: number
  secondaryCount: number
  emotionCount: number
  longtailCount: number
  aliasCount: number
}

interface MutableTraffic {
  totalTraffic: number
  totalCount: number
  primaryTraffic: number
  primaryCount: number
  secondaryTraffic: number
  secondaryCount: number
  emotionTraffic: number
  emotionCount: number
  longtailTraffic: number
  longtailCount: number
  aliasTraffic: number
  aliasCount: number
}

function emptyTraffic(): MutableTraffic {
  return {
    totalTraffic: 0,
    totalCount: 0,
    primaryTraffic: 0,
    primaryCount: 0,
    secondaryTraffic: 0,
    secondaryCount: 0,
    emotionTraffic: 0,
    emotionCount: 0,
    longtailTraffic: 0,
    longtailCount: 0,
    aliasTraffic: 0,
    aliasCount: 0,
  }
}

function sumKeywordTraffic(
  producttypeKeys: readonly string[],
  rows: readonly ProducttypeKeywordTrafficRow[],
): ReadonlyMap<string, MutableTraffic> {
  const trafficByKey = new Map(producttypeKeys.map((key) => [key, emptyTraffic()] as const))

  for (const row of rows) {
    const traffic = trafficByKey.get(row.producttypeKey)
    if (traffic === undefined) continue

    const value = row.keyword.traffic ?? 0
    traffic.totalTraffic += value
    traffic.totalCount += 1
    if (row.keyword.isAlias) {
      traffic.aliasTraffic += value
      traffic.aliasCount += 1
    }

    switch (row.keyword.keywordType) {
      case 0:
        traffic.primaryTraffic += value
        traffic.primaryCount += 1
        break
      case 1:
        traffic.secondaryTraffic += value
        traffic.secondaryCount += 1
        break
      case 2:
        traffic.emotionTraffic += value
        traffic.emotionCount += 1
        break
      case 3:
        traffic.longtailTraffic += value
        traffic.longtailCount += 1
        break
    }
  }

  return trafficByKey
}

function emptyCounts(): MutableCounts {
  return {
    totalCount: 0,
    primaryCount: 0,
    secondaryCount: 0,
    emotionCount: 0,
    longtailCount: 0,
    aliasCount: 0,
  }
}

function countKeywords(
  producttypeKeys: readonly string[],
  rows: readonly ProducttypeCoverageKeywordRow[],
): ReadonlyMap<string, MutableCounts> {
  const counts = new Map(producttypeKeys.map((key) => [key, emptyCounts()] as const))

  for (const row of rows) {
    const rowCounts = counts.get(row.producttypeKey)
    if (rowCounts === undefined) continue

    rowCounts.totalCount += 1
    if (row.keyword.isAlias) rowCounts.aliasCount += 1

    switch (row.keyword.keywordType) {
      case 0:
        rowCounts.primaryCount += 1
        break
      case 1:
        rowCounts.secondaryCount += 1
        break
      case 2:
        rowCounts.emotionCount += 1
        break
      case 3:
        rowCounts.longtailCount += 1
        break
    }
  }

  return counts
}

export async function fetchKeywordCoveragePage(
  page: number,
  pageSize: number,
  sort: KeywordCoverageSort,
  direction: SortDirection,
  signal?: AbortSignal,
): Promise<KeywordCoveragePage> {
  const [producttypes, salesRows, countRows] = await Promise.all([
    fetchAllProducttypes(signal),
    fetchAllSales(signal),
    sort === 'sales' ? Promise.resolve([]) : fetchAllKeywordCounts(sort, signal),
  ])
  const salesByKey = new Map(salesRows.map((row) => [row.producttypeKey, row.sales365] as const))
  const countByKey = new Map(
    countRows.map((row) => [row.producttypeKey, row.keywordCount] as const),
  )
  const rankedProducttypes = producttypes.filter(isActiveProducttype).sort((left, right) => {
    const leftValue =
      sort === 'sales'
        ? (salesByKey.get(left.producttypeKey) ?? 0)
        : (countByKey.get(left.producttypeKey) ?? 0)
    const rightValue =
      sort === 'sales'
        ? (salesByKey.get(right.producttypeKey) ?? 0)
        : (countByKey.get(right.producttypeKey) ?? 0)
    const valueDifference = direction === 'asc' ? leftValue - rightValue : rightValue - leftValue
    if (valueDifference !== 0) return valueDifference

    const salesDifference =
      (salesByKey.get(right.producttypeKey) ?? 0) - (salesByKey.get(left.producttypeKey) ?? 0)
    return salesDifference !== 0
      ? salesDifference
      : left.producttypeKey.localeCompare(right.producttypeKey, 'de')
  })
  const from = (page - 1) * pageSize
  const visibleProducttypes = rankedProducttypes.slice(from, from + pageSize)
  const visibleKeys = visibleProducttypes.map((row) => row.producttypeKey)
  const keywordRows = visibleKeys.length === 0 ? [] : await fetchKeywords(visibleKeys, signal)
  const countsByKey = countKeywords(visibleKeys, keywordRows)

  const items: readonly KeywordCoverageRow[] = visibleProducttypes.map((row) => ({
    producttypeKey: row.producttypeKey,
    producttypeName: row.producttypeName,
    description: row.description,
    aiInstructions: row.aiInstructions,
    sales365: salesByKey.get(row.producttypeKey) ?? 0,
    ...(countsByKey.get(row.producttypeKey) ?? emptyCounts()),
  }))

  return { items, totalCount: rankedProducttypes.length }
}

export async function fetchKeywordTrafficCoveragePage(
  page: number,
  pageSize: number,
  sort: KeywordCoverageSort,
  direction: SortDirection,
  signal?: AbortSignal,
): Promise<KeywordTrafficCoveragePage> {
  const [producttypes, salesRows, aggregateRows] = await Promise.all([
    fetchAllProducttypes(signal),
    fetchAllSales(signal),
    sort === 'sales' ? Promise.resolve([]) : fetchAllTrafficAggregates(sort, signal),
  ])
  const salesByKey = new Map(salesRows.map((row) => [row.producttypeKey, row.sales365] as const))
  const aggregateByKey = new Map(
    aggregateRows.map((row) => [row.producttypeKey, row.traffic] as const),
  )
  const rankedProducttypes = producttypes.filter(isActiveProducttype).sort((left, right) => {
    const leftValue =
      sort === 'sales'
        ? (salesByKey.get(left.producttypeKey) ?? 0)
        : (aggregateByKey.get(left.producttypeKey) ?? 0)
    const rightValue =
      sort === 'sales'
        ? (salesByKey.get(right.producttypeKey) ?? 0)
        : (aggregateByKey.get(right.producttypeKey) ?? 0)
    const valueDifference = direction === 'asc' ? leftValue - rightValue : rightValue - leftValue
    if (valueDifference !== 0) return valueDifference

    const salesDifference =
      (salesByKey.get(right.producttypeKey) ?? 0) - (salesByKey.get(left.producttypeKey) ?? 0)
    return salesDifference !== 0
      ? salesDifference
      : left.producttypeKey.localeCompare(right.producttypeKey, 'de')
  })
  const from = (page - 1) * pageSize
  const visibleProducttypes = rankedProducttypes.slice(from, from + pageSize)
  const visibleKeys = visibleProducttypes.map((row) => row.producttypeKey)
  const keywordRows = visibleKeys.length === 0 ? [] : await fetchKeywordTraffic(visibleKeys, signal)
  const trafficByKey = sumKeywordTraffic(visibleKeys, keywordRows)

  const items: readonly KeywordTrafficCoverageRow[] = visibleProducttypes.map((row) => ({
    producttypeKey: row.producttypeKey,
    producttypeName: row.producttypeName,
    description: row.description,
    aiInstructions: row.aiInstructions,
    sales365: salesByKey.get(row.producttypeKey) ?? 0,
    ...(trafficByKey.get(row.producttypeKey) ?? emptyTraffic()),
  }))

  return { items, totalCount: rankedProducttypes.length }
}
